/**
 * B2 (E-2.J.3-02): Signal Resolution Engine
 *
 * At send time: resolve which variant per contact per content block.
 * Signal sources: scroll depth, UTM, webinar, deal stage, keywords,
 * engagement velocity. Priority rules when multiple signals match.
 *
 * Architecture: pure function, no side effects, no DB calls.
 * Caller provides the contact's signals (from ClickHouse/CRM);
 * this engine matches signals to variant rules.
 */

import type {
  BehavioralSignal,
  ContentBlock,
  ContentVariant,
  SignalMatch,
  VariantResolution,
  ContactResolutionResult,
} from "./types.js";

/**
 * Check if a signal matches a variant's signal rule.
 */
function matchesSignal(signal: BehavioralSignal, rule: SignalMatch): boolean {
  if (signal.source !== rule.source) return false;

  const signalVal = signal.value;
  const ruleVal = rule.value ?? "";

  switch (rule.operator) {
    case "eq":
      return signalVal === ruleVal;
    case "gt":
    case "lt":
    case "gte":
    case "lte": {
      const a = parseFloat(signalVal);
      const b = parseFloat(ruleVal);
      // Non-numeric values → no match (NaN comparisons always false)
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      if (rule.operator === "gt") return a > b;
      if (rule.operator === "lt") return a < b;
      if (rule.operator === "gte") return a >= b;
      return a <= b;
    }
    case "contains":
      return signalVal.toLowerCase().includes(ruleVal.toLowerCase());
    case "exists":
      return signalVal.length > 0;
    default:
      return false;
  }
}

/**
 * Resolve a single content block: find the highest-priority variant
 * whose signal rule matches any of the contact's behavioral signals.
 */
export function resolveBlock(
  block: ContentBlock,
  signals: BehavioralSignal[],
): VariantResolution {
  // Sort variants by priority descending (higher = checked first)
  const sorted = [...block.variants].sort((a, b) => b.priority - a.priority);

  for (const variant of sorted) {
    for (const signal of signals) {
      if (matchesSignal(signal, variant.signalMatch)) {
        return {
          blockId: block.id,
          selectedVariantId: variant.id,
          matchedSignal: signal,
          reason: "signal_match",
        };
      }
    }
  }

  // No match — use default variant
  return {
    blockId: block.id,
    selectedVariantId: block.defaultVariantId,
    matchedSignal: null,
    reason: "default_fallback",
  };
}

/**
 * Resolve all content blocks for a contact.
 * This is the main entry point called at send time.
 */
export function resolveAllBlocks(
  contactId: string,
  tenantId: string,
  blocks: ContentBlock[],
  signals: BehavioralSignal[],
): ContactResolutionResult {
  const resolutions = blocks.map((block) => resolveBlock(block, signals));

  return {
    contactId,
    tenantId,
    resolutions,
    resolvedAt: new Date().toISOString(),
  };
}
